import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { get, useConfig, isoDay, addDays } from "@/lib/api";
import { STATUSES } from "@/components/badges";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";

const PERIODS = [["", "À venir"], ["today", "Aujourd'hui"], ["tomorrow", "Demain"], ["7d", "7 jours"], ["30d", "30 jours"], ["past", "Inclure passés"]];
export const periodToRange = (p) => {
  const t = new Date();
  if (p === "today") return { date_from: isoDay(t), date_to: isoDay(t) };
  if (p === "tomorrow") return { date_from: isoDay(addDays(t, 1)), date_to: isoDay(addDays(t, 1)) };
  if (p === "7d") return { date_from: isoDay(t), date_to: isoDay(addDays(t, 6)) };
  if (p === "30d") return { date_from: isoDay(t), date_to: isoDay(addDays(t, 29)) };
  if (p === "past") return { include_past: true };
  return {};
};

const Section = ({ title, children }) => (
  <div className="py-3 border-b border-slate-200 dark:border-slate-800"><div className="overline mb-2">{title}</div>{children}</div>
);
const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className={`text-[11px] px-1.5 py-0.5 rounded-[3px] border transition-colors duration-150 ${active ? "bg-slate-900 text-white border-slate-900 dark:bg-slate-100 dark:text-slate-900" : "border-slate-200 dark:border-slate-700 hover:border-slate-400"}`}>{children}</button>
);

export const Filters = ({ f, set, toggleList, reset, minimal = false }) => {
  const { data: cfg } = useConfig();
  const { data: communes } = useQuery({ queryKey: ["communes-mon"], queryFn: () => get("/communes", { monitored: true }) });
  const { data: territories } = useQuery({ queryKey: ["territories"], queryFn: () => get("/territories") });
  return (
    <div className="text-xs" data-testid="filters-panel">
      <div className="flex items-center justify-between pb-2"><span className="font-display font-bold text-sm">Filtres</span>
        <Button data-testid="filters-reset" variant="ghost" size="sm" className="h-6 text-[11px]" onClick={reset}><X className="h-3 w-3 mr-1" />Réinitialiser</Button></div>
      <Input data-testid="filter-q" placeholder="Recherche plein texte…" className="h-8 text-xs" value={f.q || ""} onChange={(e) => set({ q: e.target.value })} />
      <Section title="Période">
        <div className="flex flex-wrap gap-1">{PERIODS.map(([k, l]) => <Chip key={k} testId={`filter-period-${k || "upcoming"}`} active={(f.period || "") === k} onClick={() => set({ period: k, date_from: "", date_to: "" })}>{l}</Chip>)}</div>
        <div className="flex gap-1 mt-2">
          <Input data-testid="filter-date-from" type="date" className="h-7 text-[11px]" value={f.date_from || ""} onChange={(e) => set({ date_from: e.target.value, period: "custom" })} />
          <Input data-testid="filter-date-to" type="date" className="h-7 text-[11px]" value={f.date_to || ""} onChange={(e) => set({ date_to: e.target.value, period: "custom" })} />
        </div>
      </Section>
      {!minimal && (
        <Section title="Signaux">
          {[["is_new", "Nouveaux (48 h)"], ["is_modified", "Modifiés"], ["weak", "Signaux faibles"], ["contradiction", "Contradictions"]].map(([k, l]) => (
            <label key={k} className="flex items-center justify-between py-0.5">{l}<Switch data-testid={`filter-${k}`} checked={!!f[k]} onCheckedChange={(v) => set({ [k]: v })} /></label>
          ))}
        </Section>
      )}
      <Section title="Statut">
        <div className="flex flex-wrap gap-1">{STATUSES.map((s) => <Chip key={s} testId={`filter-status-${s}`} active={(f.status || []).includes(s)} onClick={() => toggleList("status", s)}>{s}</Chip>)}</div>
      </Section>
      <Section title="Typologie">
        <div className="flex flex-wrap gap-1">{cfg && Object.entries(cfg.groups).map(([k, l]) => <Chip key={k} testId={`filter-cat-${k}`} active={(f.category || []).includes(k)} onClick={() => toggleList("category", k)}>{l}</Chip>)}</div>
      </Section>
      <Section title="Public / accès">
        <div className="flex flex-wrap gap-1 mb-1.5">
          {[["", "Tous"], ["1", "Professionnel"], ["0", "Grand public"]].map(([k, l]) => <Chip key={k} testId={`filter-pro-${k || "all"}`} active={(f.pro || "") === k} onClick={() => set({ pro: k })}>{l}</Chip>)}
        </div>
        <div className="flex flex-wrap gap-1">
          {[["", "Tous tarifs"], ["gratuit", "Gratuit"], ["payant", "Payant"], ["inconnu", "Inconnu"]].map(([k, l]) => <Chip key={k} testId={`filter-access-${k || "all"}`} active={(f.access || "") === k} onClick={() => set({ access: k })}>{l}</Chip>)}
        </div>
      </Section>
      <Section title="Confiance">
        <div className="flex flex-wrap gap-1">
          {[["", "Toutes"], ["80", "Élevée ≥80"], ["50", "≥ Moyenne"], ["verify", "À vérifier <50"]].map(([k, l]) => <Chip key={k} testId={`filter-conf-${k || "all"}`} active={(f.conf || "") === k} onClick={() => set({ conf: k })}>{l}</Chip>)}
        </div>
      </Section>
      <Section title={`Intérêt territorial ≥ ${f.min_interest || 0}`}>
        <Slider data-testid="filter-min-interest" min={0} max={100} step={5} value={[Number(f.min_interest || 0)]} onValueChange={([v]) => set({ min_interest: v || "" })} />
      </Section>
      <Section title="Territoire">
        <select data-testid="filter-territory" className="w-full h-7 border border-slate-200 dark:border-slate-700 bg-transparent rounded-[3px] text-xs px-1" value={f.territory_id || ""} onChange={(e) => set({ territory_id: e.target.value })}>
          <option value="">Toutes les communes surveillées</option>
          {(territories || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <div className="max-h-40 overflow-y-auto mt-2 space-y-0.5">
          {(communes || []).map((c) => (
            <label key={c.insee} className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" data-testid={`filter-commune-${c.insee}`} checked={(f.insee || []).includes(c.insee)} onChange={() => toggleList("insee", c.insee)} />
              {c.name} <span className="font-mono text-[10px] text-slate-400">{c.upcoming_events}</span>
            </label>
          ))}
        </div>
      </Section>
    </div>
  );
};

export const filtersToParams = (f) => {
  const p = { q: f.q, status: f.status, category: f.category, insee: f.insee, territory_id: f.territory_id, access: f.access,
    pro: f.pro === "1" ? "1" : f.pro === "0" ? "0" : undefined, min_interest: f.min_interest, is_new: f.is_new, is_modified: f.is_modified,
    weak: f.weak, contradiction: f.contradiction, organizer_id: f.organizer_id, venue_id: f.venue_id, since: f.since, source_id: f.source_id,
    ...(f.period === "custom" ? { date_from: f.date_from, date_to: f.date_to, include_past: !!f.date_from } : periodToRange(f.period)) };
  if (f.conf === "80") p.min_conf = 80;
  if (f.conf === "50") p.min_conf = 50;
  if (f.conf === "verify") p.max_conf = 49;
  return p;
};
